/**
 * This file is used to bootstrap the client-side app.
 *
 * - Create a store using the preloaded state
 * - Renders the AppProvider
 * - Sets up HMR
 *
 */
// Add Set polyfill to support Browsers that don't support set and map
import 'core-js/es/set';
import 'core-js/es/map';

import React from 'react';
import { render } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { AppContainer } from 'react-hot-loader';
import Immutable from 'immutable';
import { Provider } from 'react-redux';

import AppProvider from './app_provider';

import { configureStore } from './store';
import { getRoutes } from './routes';

/**
 * Method used to initialize the store from the server.
 * @returns {Object} - the store for the server.
 */
const preloadState = () => {
    const preloadedState = JSON.parse(atob(window.__PRELOADED_STATE__));
    delete window.__PRELOADED_STATE__;
    return configureStore(Immutable.fromJS(preloadedState));
};

/**
 * Method that put all the bits together - AppContainer, AppProvider and the store,
 * along with a DOM mounting point.
 * @param  {Node} appProvider - The AppProvider component to render
 * @returns {Component} - The fully configured app component
 */

const container = document.getElementById('root');
const root = createRoot(container);
const getAppContainerRenderer = (appProvider) => {
    return root.render(
        <AppContainer>
            {appProvider}
        </AppContainer>,
    );
};

/**
 * Method to Initialize WhyDidYouUpdate logging for client
 * See: https://github.com/maicki/why-did-you-update
 */
const initWhyDidYouUpdate = () => {
    if (process.env.EVEREST_DEPLOY_ENV === 'local' && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line global-require
        const { whyDidYouUpdate } = require('why-did-you-update');
        whyDidYouUpdate(React);
    }
};

// Initialize WhyDidYouUpdate Performance Plugin - Uncomment initWhyDidYouUpdate() below to use when required locally
// initWhyDidYouUpdate();

// This is a workaround to fix the error: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.
// See more details: https://dev.azure.com/ctmaus/CTM/_workitems/edit/37579
if (typeof Node === 'function' && Node.prototype) {
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function (child, ...rest) {
        if (child.parentNode !== this) {
            const errMsg = 'Cannot remove a child from a different parent';
            if (console) {
                // eslint-disable-next-line no-console
                console.error(errMsg, child, this);
            }
            return child;
        }
        return originalRemoveChild.apply(this, [child, ...rest]);
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function (newNode, referenceNode, ...rest) {
        if (referenceNode && referenceNode.parentNode !== this) {
            const errMsg = 'Cannot insert before a reference node from a different parent';
            if (console) {
                // eslint-disable-next-line no-console
                console.error(errMsg, referenceNode, this);
            }
            return newNode;
        }
        return originalInsertBefore.apply(this, [newNode, referenceNode, ...rest]);
    };
}

// Initialize store from the server
const store = preloadState();
// Initialize AppContainer renderer
getAppContainerRenderer(
    <Provider store={store}>
        <AppProvider routes={getRoutes(store, false)} />
    </Provider>
);

// Setup forEach loops with polyfill, see https://stackoverflow.com/questions/53331180/babel-polyfill-being-included-but-foreach-still-doesnt-work-in-ie11-on-nodelis
if (window.NodeList && !NodeList.prototype.forEach) {
    NodeList.prototype.forEach = Array.prototype.forEach;
}

// Listen for dev changes and run HMR
if (module.hot) {
    module.hot.accept(['./app_provider', './routes', './store'], () => {
        // eslint-disable-next-line global-require
        const ProviderHot = require('./app_provider').default;
        // eslint-disable-next-line global-require
        const routes = require('./routes').getRoutes(store, false);
        getAppContainerRenderer(
            <Provider store={store}>
                <ProviderHot routes={routes} />
            </Provider>
        );
    });
}
